// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- Actions & Hooks
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useJobTaskActions } from 'datastage/store/jobTasks/useJobTasks';
// --- Components
import { JobMetricsModal } from '../modals/metrics';
import { JobTasksModal } from '../modals/tasks';
// --- Utils
import { useJobsGridUtils } from './utils/gridUtils';
// --- External UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

/**
 * Jobs Grid
 *
 * Features:
 * - Displays a data grid of jobs using AG Grid
 * - Integrates with Redux for jobs state management
 * - Opens modals for job metrics and job tasks
 *
 * @returns {JSX.Element}
 */
export const JobsGrid = () => {
    const agGridApi = useAgGridApi();
    const { jobs } = useSelector((s) => s.jobs);
    const { updateJobTasks, updateJobTasksForm } = useJobTaskActions();
    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    const onOpenJobTasksModal = useCallback(
        (job) => {
            if (!job || !job.id) return;

            updateJobTasks({
                selectedJob: job,
                isModalOpen: true,
            });

            updateJobTasksForm({
                key: 'filterForm',
                data: { jobId: job.id },
            });
        },
        [updateJobTasks, updateJobTasksForm],
    );

    // Grid utilities for sorting and columns
    const { columns } = useJobsGridUtils({
        columnHandlers: [onOpenJobTasksModal],
    });


    return (
        <>
            {/* AG Grid Table for displaying jobs */}
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: jobs,
                    headerHeight: 45,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No Jobs Found'}
            />
            <JobMetricsModal />
            <JobTasksModal />
        </>
    );
};
